import { Component, inject, signal, Signal, ChangeDetectionStrategy } from "@angular/core";
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from "@angular/forms";
import { TranslateModule, TranslateService } from "@ngx-translate/core";
import { DynamicDialogConfig, DynamicDialogRef } from "@openng/optimus-ui/dynamicdialog";
import { InputTextModule } from "@openng/optimus-ui/inputtext";
import { ButtonComponent } from "../../../../../shared/components/button/button.component";
import { MessageService } from "@openng/optimus-ui/api";
import { CheckboxModule } from "@openng/optimus-ui/checkbox";
import { TooltipModule } from "@openng/optimus-ui/tooltip";
import { RolesService } from "../../../../../services/api/roles/roles.service";

import { toSignal } from "@angular/core/rxjs-interop";

import { UserService } from "../../../../../services/api/user/user.service";
import { Role } from "../../../../../models/api/role";
import { User } from "../../../../../models/api/user";
import { first, map } from "rxjs";
import { WIKI_ACCESS_LEVEL_OPTIONS } from "../../../../../constants/wikiAccessLevelOptions";
import { DropdownOption } from "../../../../../models/ui/dropdownOption";
import { EMAIL_PATTERN } from "../../../../../constants/regExp";
import { TextareaModule } from "@openng/optimus-ui/textarea";
import { SelectModule } from "@openng/optimus-ui/select";

@Component({
  standalone: true,
  selector: "app-user",
  imports: [
    ReactiveFormsModule,
    InputTextModule,
    TextareaModule,
    TranslateModule,
    ButtonComponent,
    CheckboxModule,
    SelectModule,
    TooltipModule,
  ],
  templateUrl: "./user.component.html",
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: "./user.component.scss",
})
export class UserComponent {
  private config = inject(DynamicDialogConfig);
  private user = this.config.data.user;
  private actionType: "edit" | "add" = this.config.data.actionType;
  private customerId = this.config.data.customerId;
  private tenantId = this.config.data.tenantId;
  private userService = inject(UserService);
  public form = new FormGroup({
    email: new FormControl(
      {
        value: this.user ? this.user.email : null,
        disabled: this.actionType === "edit",
      },
      [Validators.required, Validators.pattern(EMAIL_PATTERN)]
    ),
    firstName: new FormControl(this.user ? this.user.firstName : null, [Validators.required]),
    lastName: new FormControl(this.user ? this.user.lastName : null, [Validators.required]),
    twoFactorEnabled: new FormControl(this.user ? this.user.twoFactorEnabled : false),
    phone: new FormControl(this.user ? this.user.phone : null),
    notes: new FormControl(this.user && this.user.notes ? this.user.notes : " "),
    role: new FormControl(this.user ? this.user.role : null, [Validators.required]),
    faultListPermission: new FormControl(this.user ? this.user.faultListPermission : null, Validators.required),
  });
  public translate = inject(TranslateService);
  public ref = inject(DynamicDialogRef);
  private message = inject(MessageService);
  public requestStarted = signal(false);
  private rolesService = inject(RolesService);

  public roles: Signal<Role[] | undefined> = toSignal(
    this.rolesService.getRoles(this.tenantId, this.customerId).pipe(
      map((roles) => {
        if (!this.customerId) {
          return roles.filter((role) => role.label !== "CUSTOMER_ADMIN");
        } else {
          return roles;
        }
      })
    )
  );

  public wikiLevelOptions: DropdownOption[] = WIKI_ACCESS_LEVEL_OPTIONS;

  /**
   * Validates the form and saves the user. If the form is invalid, displays an error message.
   * If the action type is "add", creates a new user. If the action type is "edit", updates the user.
   */
  public save() {
    const formValues = this.form?.getRawValue();

    if (this.form.valid && formValues) {
      if (!this.requestStarted()) {
        this.requestStarted.set(true);
        if (this.actionType === "add") {
          this.user = { ...formValues };
          this.createUser(this.user);
        } else if (this.actionType === "edit") {
          this.user = { id: this.user.id, ...formValues };
          this.editUser(this.user);
        }
      }
    } else {
      this.message.add({
        key: "global",
        severity: "error",
        summary: this.translate.instant("common.error"),
        detail: this.translate.instant("common.invalid_form"),
        life: 4500,
      });
    }
  }

  /**
   * Submits the form if valid and no request is already in progress. Creates a new user. Shows an error message if the form is not valid.
   * @param formValues The user data to be created
   */
  private createUser(formValues: User) {
    if (this.customerId) {
      this.userService
        .addUserFromCustomer(formValues, this.customerId)
        .pipe(first())
        .subscribe({
          next: () => {
            this.requestStarted.set(false);
            this.ref.close(true);
          },
          error: () => {
            this.requestStarted.set(false);
          },
        });
    } else {
      this.userService
        .addUserFromTenant(formValues, this.tenantId)
        .pipe(first())
        .subscribe({
          next: () => {
            this.requestStarted.set(false);
            this.ref.close(true);
          },
          error: () => {
            this.requestStarted.set(false);
          },
        });
    }
  }

  /**
   * Submits the form if valid and no request is already in progress. Updates the user. Shows an error message if the form is not valid.
   * @param formValues The user data to be updated
   */
  private editUser(formValues: User) {
    this.userService
      .editUser(formValues)
      .pipe(first())
      .subscribe({
        next: () => {
          this.requestStarted.set(false);
          this.ref.close(true);
        },
        error: () => {
          this.requestStarted.set(false);
        },
      });
  }
}
